import { IconAlertTriangle } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { useId, useState } from "react";
import { PDeleteConfirmation } from "../PDeleteConfirmation/PDeleteConfirmation";
import { LossesAckCheckbox } from "../PDeleteCompany/components/LossesAckCheckbox";
import { describeDissolve } from "@/components/PDeleteCompany/dissolveText";
import { lossesAck, type LossesAck } from "@/lib/legal/dissolveLossesAck";
import {
  preflightLosesEvents,
  type CompanyChangeOutcome,
  type PreflightResult,
} from "./usePNotifications";

interface JoinCompanyDialogProps {
  /** The join preflight this dialog asks about. Render with `key={result.notificationId}`. */
  result: PreflightResult;
  onCloseAction: () => void;
  confirmCompanyJoin: (
    newCompanyId: string,
    options?: { lossesAck?: LossesAck | null },
  ) => Promise<CompanyChangeOutcome>;
  /** The bell's failure sentence for a backend code (an allowlist, else `fallback`). */
  describeFailure: (failure: { code: string; detail?: string }, fallback: string) => string;
}

/**
 * The bell's join dialog — accepting an organisation invitation, which for a
 * solo owner dissolves the current organisation (PB-CDP F4).
 *
 * Built from parts (offboarding MED-5): each warning shows only when its own
 * field is true. `willDeleteCompany` ALWAYS shows the delete warning; a new
 * backend (`describeDissolve`'s marker) then says why the switch cannot go
 * ahead (`blockText`: Confirm off) or what it forfeits (`lossesNotice`); its
 * legacy `hasActiveSubscriptions` is not read — it is true for one-time event
 * plans too, which no longer refuse anything. An old backend keeps today's
 * subscription sentence.
 *
 * PB-CDP-7: under a loss notice sits the required tick ("lapse + no refund");
 * Confirm stays off — focusable, described by the tick's label — until it is
 * ticked, and only then does the switch carry the acknowledgement
 * (`lossesAck(locale, shown)`). The tick belongs to ONE preflight: the bell
 * renders this dialog with `key={notificationId}`, so another invitation's
 * dialog never inherits it.
 */
export function JoinCompanyDialog({
  result,
  onCloseAction,
  confirmCompanyJoin,
  describeFailure,
}: JoinCompanyDialogProps) {
  const t = useTranslations("popup-notifications");
  // The dissolve gate's reason and loss texts live in `userSettings`
  // (`dissolveBlock.switch.*`, `dissolveLosses.*`); the locale formats the
  // CANCELLING date and is the acknowledgement's locale.
  const tUser = useTranslations("userSettings");
  const locale = useLocale();
  const [acked, setAcked] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<{ code: string; detail?: string } | null>(null);
  const ackLabelId = useId();

  const preflight = result.preflight;
  const dissolve = preflight.blocked ? null : describeDissolve(tUser, preflight, "switch", locale);
  const blockText = dissolve?.blockText ?? null;
  const lossesNotice = dissolve?.lossesNotice ?? null;
  const awaitingAck = lossesNotice !== null && !acked;

  const message = (() => {
    if (preflight.blocked) return t("joinCompanyBlocked");
    const parts: string[] = [];
    if (preflight.willDeleteCompany === true) {
      parts.push(t("joinCompanyDeleteWarning"));
    }
    if (!dissolve?.isNewBackend && preflight.hasActiveSubscriptions === true) {
      parts.push(t("joinCompanySubscriptionWarning"));
    }
    if (preflightLosesEvents(preflight)) {
      const count = preflight.eventsLost as number;
      parts.push(
        result.organisationName
          ? t("joinLosesEvents", { count, organisation: result.organisationName })
          : t("joinLosesEventsNoName", { count }),
      );
    }
    return parts.join(" ");
  })();

  return (
    <PDeleteConfirmation
      title={t("joinCompanyWarningTitle")}
      message={message}
      // The bell unmounts this dialog on close: its tick, error and spinner
      // go with it (and `key` gives another invitation a fresh one).
      onCloseAction={onCloseAction}
      onConfirmAction={async () => {
        if (preflight.blocked) {
          onCloseAction();
          return;
        }
        // A dissolve block and a missing tick keep Confirm off
        // (confirmDisabled below); this is the second lock, not the
        // user-facing one.
        if (blockText || awaitingAck) return;
        // Captured at the click: the notice shown and ticked, in the UI's
        // locale, naming the sentence that showed. No notice → nothing.
        const sent =
          dissolve?.acknowledgeLosses && acked && dissolve.shown
            ? lossesAck(locale, dissolve.shown)
            : null;
        setIsConfirming(true);
        setConfirmError(null);
        const outcome = await confirmCompanyJoin(result.notificationId, { lossesAck: sent });
        if (outcome.ok) {
          window.location.href = "/";
        } else {
          setIsConfirming(false);
          // Hold the backend code, not a fixed i18n key — the dialog
          // resolves it through the `errors` namespace below.
          setConfirmError({ code: outcome.code, detail: outcome.detail });
        }
      }}
      isLoading={isConfirming}
      error={confirmError ? describeFailure(confirmError, t("joinCompanyFailed")) : undefined}
      primaryButtonText={preflight.blocked ? undefined : t("confirm")}
      confirmDisabled={blockText !== null || awaitingAck}
      focusableWhenDisabled={awaitingAck}
      confirmDescribedBy={awaitingAck ? ackLabelId : undefined}
    >
      {blockText ? (
        <p
          className="body-2-regular font-error w-full whitespace-normal"
          data-testid="join-dissolve-block"
        >
          {blockText}
        </p>
      ) : lossesNotice ? (
        // What the dissolve forfeits, styled like the event delete's
        // paid-plan notice (EventDeletePaidPlanNotice: PBanner's warning
        // tone). Its text is static content: role="note", never an alert —
        // the dialog's one alert is its error line. The required tick at
        // its bottom, behind a divider (PB-CDP-7).
        <div
          data-testid="join-dissolve-losses-notice"
          className="w-full px-3 py-2.5 rounded-xl border surface-status-amber-bg outline-status-amber"
        >
          <div role="note" className="w-full flex items-start gap-2 body-3-regular font-primary">
            <IconAlertTriangle
              size={16}
              className="shrink-0 mt-px font-status-amber-dark"
              aria-hidden="true"
            />
            <span>{lossesNotice}</span>
          </div>
          <LossesAckCheckbox
            checked={acked}
            onChangeAction={setAcked}
            labelId={ackLabelId}
            disabled={isConfirming}
          />
        </div>
      ) : null}
    </PDeleteConfirmation>
  );
}
